import { NumberOutlined } from "@ant-design/icons";
import { Alert, Button, Input, Modal, Select, Space, Tag, Typography } from "antd";
import axios from "axios";
import useWebsiteSettings from "hooks/useWebsiteSettings";
import { BatchConfig } from "models/websiteSettings";
import { useState } from "react";

const { Text } = Typography;

type BatchUpdate = Pick<BatchConfig, 'prefixes' | 'current'>;

const BatchConfigView = ({
    title,
    prefixes,
    prefixSize,
    maxBatchSize,
    availablePrefixes,
    current,
    onChange
}: BatchConfig & { title: string, onChange: (update: BatchConfig) => void }) => {
    const [bn, setBN] = useState<string>(current);

    const update = (value: string) => {
        setBN(value);
        onChange({ prefixes, prefixSize, maxBatchSize, availablePrefixes, current: value });
    }

    return <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Space direction="vertical" size={0}>
            <Text>{title}</Text>
            {prefixSize === 1 && <Text type="secondary">{prefixes}</Text>}
            {prefixSize > 1 && <Tag color="geekblue">{prefixes}</Tag>}
        </Space>
        <Input value={bn} onChange={e => update(e.currentTarget.value)} />
    </Space>;
}

const BatchNumberManagement = () => {
    const [open, setOpen] = useState(false);
    const {
        settings,
        updateBatchNumbers,
        isUpdatingBatchNumber,
        isLoading,
        refetch
    } = useWebsiteSettings();
    const { batchSetting } = settings ?? {};
    const { legacy, scannable, nonScannable, special = [], version = 0 } = batchSetting ?? {};
    const [selected, setSelected] = useState<BatchConfig>();
    const [updates, setUpdates] = useState<BatchUpdate[]>([]);
    const [error, setError] = useState<{ message: string, showRefresh: boolean }>();

    const close = async () => {
        setOpen(false);
        await reset();
    }

    const reset = async () => {
        await refetch();
        setSelected(undefined);
        setUpdates([]);
        setError(undefined);
    }

    const save = async () => {
        setError(undefined);

        try {
            await updateBatchNumbers({
                expectedVersion: version,
                updates: updates.reduce((a, v) => ({ ...a, [v.prefixes]: v.current }), {})
            });

            await close();
        } catch (error) {
            let message: string;
            let showRefresh = false;

            if (axios.isAxiosError(error)) {
                const err = error.response!.data as any;

                if (error.response?.status === 409) {
                    message = "Batch numbers have changed since you opened the modal, you'll have to refresh & try again."
                    showRefresh = true;
                } else {
                    message = err.title;
                }
            } else {
                message = "An unknown error occurred";
            }

            setError({ message, showRefresh });
        }
    }

    const update = (value: string) => {
        if (!selected) {
            return;
        }

        const updated = { ...selected, current: value };
        setSelected(updated);
        trackUpdate(updated);
    }

    const onConfigChange = (value: string) => {
        const item = special.find(x => x.prefixes === value);
        setSelected(item);
    }

    const trackUpdate = (config: BatchConfig) => {
        const otherUpdates = updates.filter(x => x.prefixes !== config.prefixes);
        setUpdates([config, ...otherUpdates]);
    }

    return <>
        <Button type="link" icon={<NumberOutlined />} onClick={() => setOpen(true)}>Manage Batch Numbers</Button>
        {batchSetting && <Modal
            title="Manage Batch Numbers"
            open={open}
            closable={false}
            keyboard={false}
            maskClosable={false}
            destroyOnClose
            onOk={save}
            okButtonProps={{ disabled: updates.length === 0 }}
            onCancel={close}
            confirmLoading={isUpdatingBatchNumber || isLoading}>
            <Space direction="vertical" size={20} style={{ width: '100%' }}>
                <BatchConfigView title="Legacy" {...legacy!} onChange={trackUpdate} />
                <BatchConfigView title="Scannable" {...scannable!} onChange={trackUpdate} />
                <BatchConfigView title="Non-Scannable" {...nonScannable!} onChange={trackUpdate} />
                <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Space direction="vertical" size={0}>
                        <Text>Custom Batch Prefixes</Text>
                        <Select
                            placeholder="- Select a Prefix -"
                            options={special.map(c => ({ label: c.prefixes, value: c.prefixes }))}
                            onChange={onConfigChange}
                        />
                    </Space>
                    <Input
                        disabled={!selected}
                        value={selected?.current}
                        onChange={(e) => update(e.currentTarget.value)}
                    />
                </Space>
                {error && <Alert
                    type="warning"
                    description={<>
                        {error.message}
                        {error.showRefresh && <Button type="link" onClick={async () => { await reset() }}>Refresh</Button>}
                    </>}
                />}
            </Space>
        </Modal>}
    </>;
}

export default BatchNumberManagement;